/**
 * substitutionLogic.js - Gestione sostituzioni e ordine assalti
 * 
 * Funzioni per gestire:
 * - Sostituzioni tiratori con possibilità di re-ingresso
 * - Riordino assalti
 * - Salto assalti
 */

/**
 * Applica una sostituzione a tutti gli assalti futuri
 * @param {Array} assalti - Lista degli assalti
 * @param {string} teamSide - 'A' o 'B' 
 * @param {Object} originale - Tiratore da sostituire
 * @param {Object} sostituto - Tiratore sostituto
 * @param {number} dalAssalto - Indice dell'assalto da cui applicare
 * @returns {Object} { assalti, sostituzione }
 */
export function applySubstitution(assalti, teamSide, originale, sostituto, dalAssalto) {
    const updatedAssalti = assalti.map((assalto, index) => {
        // Non modificare assalti già completati o precedenti
        if (index < dalAssalto || assalto.completed) {
            return assalto;
        }

        const fencerKey = teamSide === 'A' ? 'fencerA' : 'fencerB';
        const actualKey = teamSide === 'A' ? 'actualFencerA' : 'actualFencerB';

        // Verifica se questo assalto coinvolge il tiratore originale
        const currentFencer = assalto[actualKey] || assalto[fencerKey];
        if (currentFencer && currentFencer.id === originale.id) {
            return {
                ...assalto,
                [actualKey]: { ...sostituto }
            };
        }

        return assalto;
    });

    const sostituzione = {
        teamSide,
        originale: { ...originale },
        sostituto: { ...sostituto },
        dalAssalto,
        alAssalto: null, // null = fino alla fine
        timestamp: Date.now(),
        tipo: 'sostituzione'
    };

    return { assalti: updatedAssalti, sostituzione };
}

/**
 * Ripristina un tiratore (re-ingresso) da un certo assalto
 * @param {Array} assalti - Lista degli assalti
 * @param {string} teamSide - 'A' o 'B'
 * @param {Object} tiratore - Tiratore da ripristinare
 * @param {Object} sostituito - Tiratore attualmente in campo da rimuovere
 * @param {number} dalAssalto - Indice dell'assalto da cui ripristinare
 * @returns {Object} { assalti, sostituzione }
 */
export function revertSubstitution(assalti, teamSide, tiratore, sostituito, dalAssalto) {
    const updatedAssalti = assalti.map((assalto, index) => {
        if (index < dalAssalto || assalto.completed) {
            return assalto;
        }

        const fencerKey = teamSide === 'A' ? 'fencerA' : 'fencerB';
        const actualKey = teamSide === 'A' ? 'actualFencerA' : 'actualFencerB';

        // Se l'assalto originale era del tiratore che rientra
        if (assalto[fencerKey] && assalto[fencerKey].id === tiratore.id) {
            return {
                ...assalto,
                [actualKey]: null // Rimuove la sostituzione, torna all'originale
            };
        }

        return assalto;
    });

    const sostituzione = {
        teamSide,
        originale: { ...sostituito },
        sostituto: { ...tiratore },
        dalAssalto,
        alAssalto: null,
        timestamp: Date.now(),
        tipo: 'reingresso'
    };

    return { assalti: updatedAssalti, sostituzione };
}

/**
 * Segna un assalto come "saltato"
 * @param {Array} assalti - Lista degli assalti
 * @param {number} assaltoIndex - Indice dell'assalto da saltare
 * @returns {Array} Assalti aggiornati
 */
export function skipAssalto(assalti, assaltoIndex) {
    return assalti.map((assalto, index) => {
        if (index === assaltoIndex && !assalto.completed) {
            return {
                ...assalto,
                skipped: true
            };
        }
        return assalto;
    });
}

/**
 * Riattiva un assalto precedentemente saltato
 * @param {Array} assalti - Lista degli assalti
 * @param {number} assaltoIndex - Indice dell'assalto da riattivare
 * @returns {Array} Assalti aggiornati
 */
export function unskipAssalto(assalti, assaltoIndex) {
    return assalti.map((assalto, index) => {
        if (index === assaltoIndex) {
            return {
                ...assalto,
                skipped: false
            };
        }
        return assalto;
    });
}

/**
 * Riordina gli assalti spostando un assalto da una posizione all'altra
 * Solo assalti non completati possono essere spostati
 * @param {Array} assalti - Lista degli assalti
 * @param {number} fromIndex - Posizione di partenza
 * @param {number} toIndex - Posizione di arrivo
 * @returns {Array} Assalti riordinati
 */
export function reorderAssalti(assalti, fromIndex, toIndex) {
    // Non permettere di spostare assalti completati
    if (assalti[fromIndex]?.completed) {
        console.warn('Non è possibile spostare un assalto completato');
        return assalti;
    }

    // Trova il primo assalto non completato
    const firstIncomplete = assalti.findIndex(a => !a.completed && !a.skipped);

    // Non permettere di spostare prima di assalti completati
    if (toIndex < firstIncomplete) {
        toIndex = firstIncomplete;
    }

    const result = [...assalti];
    const [removed] = result.splice(fromIndex, 1);
    result.splice(toIndex, 0, removed);

    return result;
}

/**
 * Trova il prossimo assalto da tirare (saltando quelli skipped e completed)
 * @param {Array} assalti - Lista degli assalti
 * @param {number} currentIndex - Indice corrente (-1 se non iniziato)
 * @returns {number} Indice del prossimo assalto, -1 se finito
 */
export function getNextAssalto(assalti, currentIndex = -1) {
    for (let i = currentIndex + 1; i < assalti.length; i++) {
        if (!assalti[i].completed && !assalti[i].skipped) {
            return i;
        }
    }
    return -1; // Nessun assalto disponibile
}

/**
 * Ottiene il tiratore effettivo per un assalto (considerando sostituzioni)
 * @param {Object} assalto - L'assalto
 * @param {string} teamSide - 'A' o 'B'
 * @returns {Object} Tiratore effettivo
 */
export function getActualFencer(assalto, teamSide) {
    const fencerKey = teamSide === 'A' ? 'fencerA' : 'fencerB';
    const actualKey = teamSide === 'A' ? 'actualFencerA' : 'actualFencerB';

    return assalto[actualKey] || assalto[fencerKey];
}

/**
 * Conta gli assalti rimanenti (non completati e non skipped)
 * @param {Array} assalti - Lista degli assalti
 * @returns {number} Numero di assalti rimanenti
 */
export function countRemainingAssalti(assalti) {
    return assalti.filter(a => !a.completed && !a.skipped).length;
}

/**
 * Genera un riepilogo delle sostituzioni per lo storico
 * @param {Array} sostituzioni - Lista delle sostituzioni
 * @returns {Array} Lista formattata per visualizzazione
 */
export function formatSubstitutionHistory(sostituzioni) {
    return sostituzioni.map(s => ({
        ...s,
        label: s.tipo === 'reingresso'
            ? `Assalto ${s.dalAssalto + 1}: ${s.sostituto.name} rientra (era ${s.originale.name})`
            : `Assalto ${s.dalAssalto + 1}: ${s.originale.name} → ${s.sostituto.name}`,
        icon: s.tipo === 'reingresso' ? '🔙' : '🔄'
    }));
}

/**
 * Verifica se un tiratore è attualmente in campo (non sostituito)
 * @param {Array} assalti - Lista degli assalti
 * @param {string} teamSide - 'A' o 'B'
 * @param {Object} tiratore - Tiratore da verificare
 * @returns {boolean} true se il tiratore è in campo
 */
export function isFencerActive(assalti, teamSide, tiratore) {
    // Trova il prossimo assalto non completato che coinvolge questo tiratore
    const fencerKey = teamSide === 'A' ? 'fencerA' : 'fencerB';

    for (const assalto of assalti) {
        if (assalto.completed || assalto.skipped) continue;

        if (assalto[fencerKey]?.id === tiratore.id) {
            const actual = getActualFencer(assalto, teamSide);
            return actual.id === tiratore.id;
        }
    }

    return true; // Se non ha assalti futuri, consideralo attivo
}
